import { ArrowRight, Plane, Sparkles } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85";
const LOGO =
  "https://customer-assets.emergentagent.com/job_explore-journeys-26/artifacts/4jx8ieos_PHOTO-2026-06-24-12-59-57.jpeg";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-gradient-to-br from-[#FFF8F1] via-white to-[#F3EBFF]"
    >
      {/* Decorative gradient blobs */}
      <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-[#4A1D96]/10 blur-3xl" />
      <div className="absolute top-1/3 -left-40 w-[440px] h-[440px] rounded-full bg-[#F97316]/15 blur-3xl" />

      {/* Dot grid overlay */}
      <div className="absolute inset-0 dot-grid opacity-70" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20 min-h-screen flex flex-col justify-center">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Left: copy */}
          <div className="lg:col-span-7 fade-in-up">
            <div className="inline-flex items-center gap-2 bg-white border border-[#4A1D96]/15 px-4 py-2 shadow-sm">
              <Sparkles size={14} className="text-[#F97316]" />
              <span className="uppercase tracking-[0.22em] text-[11px] text-[#4A1D96] font-semibold">
                One platform · Endless journeys
              </span>
            </div>

            <h1
              data-testid="hero-heading"
              className="mt-8 font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-[#1B0F3A]"
            >
              Book anything.
              <br />
              <span className="brand-gradient italic font-medium">
                Travel everywhere.
              </span>
            </h1>

            <p className="mt-8 max-w-xl text-lg text-[#3B2A5B] leading-relaxed">
              Celesta is your single desk for flights, cabs, buses, hotels,
              trains, holidays and events — with a dedicated advisor for every
              corporate account and a 24/7 concierge that actually picks up.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <a
                href="#contact"
                data-testid="hero-primary-cta"
                className="group inline-flex items-center justify-center gap-3 bg-[#F97316] text-white px-8 py-4 text-sm uppercase tracking-[0.2em] font-semibold hover:bg-[#4A1D96] transition-colors"
              >
                Plan My Trip
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="#services"
                data-testid="hero-secondary-cta"
                className="inline-flex items-center justify-center border-2 border-[#4A1D96] text-[#4A1D96] px-8 py-4 text-sm uppercase tracking-[0.2em] font-semibold hover:bg-[#4A1D96] hover:text-white transition-colors"
              >
                Explore Services
              </a>
            </div>

            {/* Quick stats */}
            <div className="mt-16 grid grid-cols-3 gap-6 max-w-lg">
              {[
                { k: "24/7", v: "Concierge" },
                { k: "42+", v: "Global Cities" },
                { k: "7", v: "Travel Services" },
              ].map((s) => (
                <div key={s.v} data-testid={`hero-stat-${s.v.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="font-serif text-3xl sm:text-4xl text-[#4A1D96]">
                    {s.k}
                  </div>
                  <div className="mt-1 text-[11px] uppercase tracking-[0.2em] text-[#3B2A5B]/70 font-semibold">
                    {s.v}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: visual */}
          <div className="lg:col-span-5 relative fade-in-up" style={{ animationDelay: "150ms" }}>
            <div className="relative">
              {/* Main image card */}
              <div className="relative aspect-[4/5] overflow-hidden shadow-[0_30px_80px_-30px_rgba(74,29,150,0.5)]">
                <img
                  src={HERO_IMG}
                  alt="Aircraft above clouds"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#4A1D96]/40 to-transparent" />
              </div>

              {/* Floating logo badge */}
              <div className="absolute -top-8 -left-8 hidden sm:flex items-center gap-3 bg-white p-4 pr-6 shadow-xl border border-[#4A1D96]/10 float-y">
                <img src={LOGO} alt="Celesta" className="h-12 w-12 object-contain" />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#F97316] font-semibold">
                    Book Flights
                  </div>
                  <div className="font-serif text-lg text-[#4A1D96] leading-none mt-1">
                    Fly Better.
                  </div>
                </div>
              </div>

              {/* Floating trust badge */}
              <div className="absolute -bottom-6 -right-4 sm:-right-8 bg-[#4A1D96] text-white p-5 max-w-[220px] shadow-xl">
                <div className="flex items-center gap-2">
                  <Plane size={14} className="text-[#FFB47A]" />
                  <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#FFB47A]">
                    Est. 2026
                  </span>
                </div>
                <div className="mt-2 font-serif text-lg leading-tight">
                  From Chennai to the world.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
